import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/employee-redux";

import { uniq, flatten, values } from "lodash";
import { Icon, Loader, Message } from "semantic-ui-react";

import {
    InitialWorkAdjustmentQuestionnaire as InitialWorkAdjustmentQuestionnaireCommon
} from "@teamprevent/common/workAdjustment/components/InitialWorkAdjustmentQuestionnaire";
import * as questionIds from "@teamprevent/common/workAdjustment/initialWorkAdjustmentQuestionnaireQuestionIds";
import { submitQuestionnaire as submitQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";
import {
    INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY,
    ParentReferralReportModel,
    InitialWorkAdjustmentQuestionnaireModel,
    noFilesUploaded
} from "@teamprevent/common/workAdjustment/models";
import { QuestionnairesState } from "@teamprevent/common/questionnaires/model";
import { toast } from "@teamprevent/common/toasts";
import { ShareDocumentsMessageDto } from "@teamprevent/common/shareDocuments/models";

import { ApplicationState } from "../../applicationState";
import { workAdjustmentApi } from "../WorkAdjustmentApi";
import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { loadInitialWorkAdjustmentQuestionnaire } from "../actions";
import { shareDocumentsApi } from "../../shareDocuments/shareDocumentsApi";

import { Location, push } from "redux-little-router";

const InitialWorkAdjustmentQuestionnaire: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [parentReferralReport, setParentReferralReport] = React.useState<ParentReferralReportModel>(null);
    const [readonly, setReadonly] = React.useState<boolean>(null);
    const [accessToParentReferralCase, setAccessToParentReferralCase] = React.useState<boolean>(null);

    const workAdjustmentCaseId = useSelector((state: { router: Location }) => state.router.params?.ohCaseId);
    const workAdjustmentCase = useSelector((state: ApplicationState) => state.ohCases?.find(c => c.id === workAdjustmentCaseId));
    // There should be only one linked (parent) referral case to the given workAdjustmentCase
    const parentReferralCaseId = workAdjustmentCase?.linkedOhCasesIds?.length === 1 ? workAdjustmentCase?.linkedOhCasesIds[0] : null;
    const initialWorkAdjustmentDetailsTaskId = useSelector((state: { router: Location }) => state.router.params?.taskId);
    const initialWorkAdjustmentDetailsTask = workAdjustmentCase?.tasks?.find(t => t.id === initialWorkAdjustmentDetailsTaskId);
    const writeReferralReportTaskId = initialWorkAdjustmentDetailsTask?.precedingTaskId;
    const customerId = useSelector((state: { router: Location }) => state.router.params?.customerId);
    const questions = useSelector((state: { questionnaires: QuestionnairesState }) =>
        uniq(flatten(state.questionnaires[INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY]?.sections?.map(section => values(section.questions)))));
    const eTag = useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY]?.eTag);
    const questionnaire: InitialWorkAdjustmentQuestionnaireModel = { questions, eTag };
    const notesFromMeetingApiUrl = `/api/workAdjustment/${workAdjustmentCaseId}/document`;

    React.useEffect(() => {
        (async () => {
            if (parentReferralCaseId && writeReferralReportTaskId) {
                const reportData = await workAdjustmentApi.getParentReferralReport(parentReferralCaseId, writeReferralReportTaskId);
                setParentReferralReport(reportData);
                setIsLoading(false);
            }
        })();
    }, [parentReferralCaseId, writeReferralReportTaskId]);

    React.useEffect(() => {
        (async () => {
            if (workAdjustmentCaseId) {
                await dispatch(loadOhCaseDetail({ ohCaseId: workAdjustmentCaseId }));
            }
        })();
    }, [workAdjustmentCaseId]);

    React.useEffect(() => {
        (async () => {
            if (parentReferralCaseId) {
                try {
                    await dispatch(loadOhCaseDetail({ ohCaseId: parentReferralCaseId }));
                    setAccessToParentReferralCase(true);
                }
                catch {
                    setAccessToParentReferralCase(false);
                }
            }
        })();
    }, [parentReferralCaseId]);

    React.useEffect(() => {
        if (initialWorkAdjustmentDetailsTask) {
            const isReadonly = initialWorkAdjustmentDetailsTask.completed;
            setReadonly(isReadonly);
        }
    }, [initialWorkAdjustmentDetailsTask]);

    const goToHomePage = () => dispatch(push("/"));

    const loadQuestionnaire = async () => {
        await dispatch(loadInitialWorkAdjustmentQuestionnaire(workAdjustmentCaseId, initialWorkAdjustmentDetailsTaskId));
    };

    const submitQuestionnaire = async (workAdjustmentQuestionnaire: InitialWorkAdjustmentQuestionnaireModel): Promise<boolean> => {
        const reasonForClosureQuestionAnswer = workAdjustmentQuestionnaire?.questions?.find(q => q.id === questionIds.ReasonForClosure)?.answer;
        if (noFilesUploaded(workAdjustmentQuestionnaire, questionIds.NotesFromMeetingUploaded, reasonForClosureQuestionAnswer)) {
            toast.warning("At least one file must be uploaded");
            return false;
        }
        const isQuestionnaireValid = workAdjustmentQuestionnaire?.questions?.every(q => q.isValid);
        if (isQuestionnaireValid) {
            await dispatch(submitQuestionnaireAction(
                workAdjustmentQuestionnaire.questions,
                `/api/workAdjustment/${workAdjustmentCaseId}/initialWorkAdjustment/${initialWorkAdjustmentDetailsTaskId}`,
                "initialWorkAdjustmentQuestionnaire",
                INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY
            ));
            goToHomePage();

            return true;
        } else {
            toast.warning("Questionnaire is invalid - please fill in all of the mandatory fields");
            return false;
        }
    };

    const onSendDocumentsToAssociate = async (model: ShareDocumentsMessageDto) => {
        await shareDocumentsApi.sendDocumentsToAssociate(workAdjustmentCase.id, model);
    };

    if (isLoading) {
        return <Loader active />;
    }

    if (accessToParentReferralCase === false) {
        return (
            <Message warning attached='top'>
                <Icon name='warning' />
                You are not assigned to the Referral Case
            </Message>
        );
    }

    if (accessToParentReferralCase) {
        return (
            <InitialWorkAdjustmentQuestionnaireCommon
                readonly={readonly}
                ohCaseId={workAdjustmentCaseId}
                taskId={initialWorkAdjustmentDetailsTaskId}
                customerId={customerId}
                employeeRecordId={workAdjustmentCase.employeeRecordId}
                questionnaire={questionnaire}
                parentReferralReport={parentReferralReport}
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={submitQuestionnaire}
                notesFromMeetingApiUrl={notesFromMeetingApiUrl}
                fileApiUrl={`/api/shareDocuments/${workAdjustmentCaseId}/document`}
                onSendDocumentsToAssociate={onSendDocumentsToAssociate}
            />
        );
    }

    return null;
};

export { InitialWorkAdjustmentQuestionnaire };
